import { createFileRoute, Link } from "@tanstack/react-router";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Download,
  Film,
  GitBranch,
  Languages,
  Loader2,
  Pause,
  Pencil,
  Play,
  Plus,
  Settings2,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  baixarCsv,
  useBoards,
  useCriarBoard,
  useCriarEstagio,
  useMoverCard,
  useMoverEstagio,
  usePausar,
  useQuadro,
  useRemoverBoard,
  useRemoverEstagio,
  useRenomearBoard,
  useRenomearEstagio,
  useRetomar,
  type CardPipeline,
  type Estagio,
  type TotalColuna,
} from "@/api/pipeline";
import { ROTULO_ETAPA } from "@/api/projetos";
import { listarProfissionais } from "@/api/registry";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { can, personalPersonId, useRole } from "@/lib/use-role";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/pipeline")({
  component: PipelinePage,
});

/**
 * Pipeline (M2) contra a API.
 *
 * O que mudou em relação ao MVP, além da origem dos dados:
 *  - os boards **segregam** projetos — o servidor devolve só os cards do board
 *    pedido, já filtrados pelo escopo do perfil (RF-11, RF-17);
 *  - os totais por coluna vêm calculados do servidor, sobre a base inteira, e
 *    não sobre o que a tela por acaso carregou (RF-12);
 *  - o badge de fase paralela finalmente aparece (RF-13);
 *  - o card navega para o id real do projeto, não para `pr1` do mock.
 */
function PipelinePage() {
  const role = useRole();
  const podeMover = can(role, "moverCard");
  const podeConfigurar = can(role, "editarParametros");
  const somenteLeitura = can(role, "somenteLeitura");
  const escopoPessoal = personalPersonId(role) !== null;

  const boards = useBoards();
  const [boardId, setBoardId] = useState<string>();
  const [coordenadorId, setCoordenadorId] = useState("");
  const [editandoEstagios, setEditandoEstagios] = useState(false);
  const [arrastando, setArrastando] = useState<string | null>(null);

  useEffect(() => {
    const existe = boards.data?.some((b) => b.id === boardId);
    if (!existe && boards.data?.length) setBoardId(boards.data[0].id);
  }, [boards.data, boardId]);

  const quadro = useQuadro(boardId, coordenadorId);
  const mover = useMoverCard();
  const criarBoard = useCriarBoard();
  const renomearBoard = useRenomearBoard();
  const removerBoard = useRemoverBoard();
  const criarEstagio = useCriarEstagio();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const coordenadores = listarProfissionais().filter((p) => p.funcao === "Coordenador de Projetos");

  const aoSoltar = (e: DragEndEvent) => {
    setArrastando(null);
    const card = quadro.data?.cards.find((c) => c.id === String(e.active.id));
    if (!e.over || !card || card.estagioId === String(e.over.id)) return;
    mover.mutate(
      { cardId: card.id, estagioId: String(e.over.id) },
      {
        onSuccess: ({ estagio }) => toast.success(`${card.cliente.nome} movido para ${estagio}`),
        onError: (erro) => toast.error(mensagem(erro)),
      },
    );
  };

  const board = quadro.data?.board;

  // Consulta desabilitada continua `pending` no React Query — não existe
  // estado "parada". Sem nenhum kanban o `boardId` nunca é definido, o quadro
  // nunca sai de `isPending` e a tela girava para sempre, sem dizer o que
  // faltava e sem caminho para criar o primeiro.
  const semKanban = !boards.isPending && !boards.isError && !boards.data?.length;
  const carregando = boards.isPending || (Boolean(boardId) && quadro.isPending);

  const novoKanban = () => {
    const nome = window.prompt("Nome do novo kanban");
    if (nome) criarBoard.mutate({ nome }, { onError: (e) => toast.error(mensagem(e)) });
  };

  const totalPor = (estagioId: string): TotalColuna =>
    quadro.data?.totais.find((t) => t.estagioId === estagioId) ?? {
      estagioId,
      cards: 0,
      slides: 0,
      horasConteudo: 0,
    };

  return (
    <>
      <TopBar
        title="Pipeline"
        subtitle={
          somenteLeitura
            ? "Visão Comercial · somente leitura"
            : "Kanbans configuráveis · pausa manual · previsto × realizado"
        }
      />

      <DndContext
        sensors={sensors}
        onDragStart={(e: DragStartEvent) => setArrastando(String(e.active.id))}
        onDragEnd={aoSoltar}
      >
        <div className="flex-1 overflow-auto p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              Kanban
              <select
                value={boardId ?? ""}
                onChange={(e) => setBoardId(e.target.value)}
                className="h-8 rounded-md border border-border bg-card px-2 text-xs text-foreground"
              >
                {semKanban && <option value="">— nenhum kanban —</option>}
                {boards.data?.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nome}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              Coordenador
              <select
                value={coordenadorId}
                onChange={(e) => setCoordenadorId(e.target.value)}
                className="h-8 rounded-md border border-border bg-card px-2 text-xs text-foreground"
              >
                <option value="">Todos</option>
                {coordenadores.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </label>

            {podeConfigurar && (
              <>
                <Button
                  size="sm"
                  variant={editandoEstagios ? "default" : "outline"}
                  className="h-8 gap-1.5 text-xs"
                  onClick={() => setEditandoEstagios((v) => !v)}
                >
                  <Settings2 className="h-3.5 w-3.5" /> Editar estágios
                </Button>

                {editandoEstagios && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1.5 text-xs"
                    onClick={novoKanban}
                  >
                    <Plus className="h-3.5 w-3.5" /> Kanban
                  </Button>
                )}

                {editandoEstagios && board && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5 text-xs"
                      onClick={() => {
                        const nome = window.prompt("Nome do novo estágio");
                        if (nome)
                          criarEstagio.mutate(
                            { boardId: board.id, nome },
                            { onError: (e) => toast.error(mensagem(e)) },
                          );
                      }}
                    >
                      <Plus className="h-3.5 w-3.5" /> Estágio
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5 text-xs"
                      onClick={() => {
                        const nome = window.prompt("Renomear kanban", board.nome);
                        if (nome)
                          renomearBoard.mutate(
                            { id: board.id, nome },
                            { onError: (e) => toast.error(mensagem(e)) },
                          );
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Renomear kanban
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5 text-xs text-health-red"
                      onClick={() =>
                        removerBoard.mutate(board.id, {
                          onSuccess: () => setBoardId(undefined),
                          onError: (e) => toast.error(mensagem(e)),
                        })
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Excluir kanban
                    </Button>
                  </>
                )}
              </>
            )}

            <div className="ml-auto flex items-center gap-2">
              {escopoPessoal && (
                <span className="rounded-md border border-border bg-secondary/40 px-3 py-1.5 text-[11px] text-muted-foreground">
                  Exibindo apenas seus projetos
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 text-xs"
                disabled={!boardId}
                onClick={() =>
                  boardId &&
                  baixarCsv(boardId, coordenadorId).catch(() => toast.error("Falha ao exportar"))
                }
              >
                <Download className="h-3.5 w-3.5" /> Exportar CSV
              </Button>
            </div>
          </div>

          {carregando && (
            <div className="flex items-center gap-2 p-4 text-[11px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando quadro…
            </div>
          )}

          {boards.isError && (
            <div className="rounded-lg border border-border p-4 text-[11px] text-health-red">
              Não foi possível carregar os kanbans. Recarregue a página.
            </div>
          )}

          {semKanban && (
            <div className="flex flex-col items-start gap-1.5 rounded-lg border border-dashed border-border p-6">
              <p className="text-sm text-foreground">Nenhum kanban criado ainda</p>
              <p className="max-w-prose text-[11px] text-muted-foreground">
                O pipeline precisa de pelo menos um quadro para receber projetos. Cada frente pode
                ter a sua própria sequência de estágios.
              </p>
              {podeConfigurar ? (
                <Button size="sm" className="mt-2 gap-1.5 text-xs" onClick={novoKanban}>
                  <Plus className="h-3.5 w-3.5" /> Criar o primeiro kanban
                </Button>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Peça a quem administra a plataforma para criar o primeiro.
                </p>
              )}
            </div>
          )}

          <div className="flex min-w-max gap-3">
            {board?.estagios.map((estagio) => (
              <Coluna
                key={estagio.id}
                estagio={estagio}
                total={totalPor(estagio.id)}
                editando={editandoEstagios}
              >
                <div className="flex flex-col gap-2 p-2">
                  {quadro.data?.cards
                    .filter((c) => c.estagioId === estagio.id)
                    .map((card) => (
                      <CartaoProjeto
                        key={card.id}
                        card={card}
                        arrastando={arrastando === card.id}
                        podeMover={podeMover}
                      />
                    ))}
                  {!quadro.data?.cards.some((c) => c.estagioId === estagio.id) && (
                    <div className="rounded-lg border border-dashed border-border/60 p-4 text-center text-[11px] text-muted-foreground">
                      Sem projetos
                    </div>
                  )}
                </div>
              </Coluna>
            ))}
          </div>
        </div>
      </DndContext>
    </>
  );
}

const mensagem = (e: unknown) => (e instanceof Error ? e.message : "Operação não permitida");

function Coluna({
  estagio,
  total,
  editando,
  children,
}: {
  estagio: Estagio;
  total: TotalColuna;
  editando: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: estagio.id });
  const renomear = useRenomearEstagio();
  const moverEstagio = useMoverEstagio();
  const remover = useRemoverEstagio();

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex w-[300px] shrink-0 flex-col rounded-xl border border-border bg-card/60 transition",
        estagio.ehPausa && "border-muted-foreground/40",
        isOver && "border-primary bg-primary/5 ring-2 ring-primary/40",
      )}
    >
      <div className="border-b border-border px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
            {estagio.nome}
          </span>
          <div className="flex items-center gap-1">
            {editando && (
              <>
                <button
                  onClick={() =>
                    moverEstagio.mutate(
                      { id: estagio.id, delta: -1 },
                      { onError: (e) => toast.error(mensagem(e)) },
                    )
                  }
                  className="text-muted-foreground hover:text-foreground"
                  title="Mover para a esquerda"
                >
                  <ArrowLeft className="h-3 w-3" />
                </button>
                <button
                  onClick={() =>
                    moverEstagio.mutate(
                      { id: estagio.id, delta: 1 },
                      { onError: (e) => toast.error(mensagem(e)) },
                    )
                  }
                  className="text-muted-foreground hover:text-foreground"
                  title="Mover para a direita"
                >
                  <ArrowRight className="h-3 w-3" />
                </button>
                <button
                  onClick={() => {
                    const nome = window.prompt("Renomear estágio", estagio.nome);
                    if (nome)
                      renomear.mutate(
                        { id: estagio.id, nome },
                        { onError: (e) => toast.error(mensagem(e)) },
                      );
                  }}
                  className="text-muted-foreground hover:text-foreground"
                  title="Renomear"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  onClick={() =>
                    remover.mutate(estagio.id, { onError: (e) => toast.error(mensagem(e)) })
                  }
                  className="text-muted-foreground hover:text-health-red"
                  title="Excluir"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </>
            )}
            <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] tabular-nums text-muted-foreground">
              {total.cards}
            </span>
          </div>
        </div>
        {/* RF-12 — a soma da coluna vem do servidor, sobre a base inteira. */}
        <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <Film className="h-3 w-3" /> {total.slides} slides
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" /> {total.horasConteudo}h cont.
          </span>
        </div>
      </div>
      {children}
    </div>
  );
}

function CartaoProjeto({
  card,
  arrastando,
  podeMover,
}: {
  card: CardPipeline;
  arrastando: boolean;
  podeMover: boolean;
}) {
  // A saída de Pausado é o botão Retomar, que exige data — nunca o arrasto.
  const travado = !podeMover || card.pausado;
  const { setNodeRef, listeners, transform } = useDraggable({ id: card.id, disabled: travado });
  const pausar = usePausar();
  const retomar = useRetomar();

  const aoRetomar = () => {
    const hoje = new Date().toISOString().slice(0, 10);
    const nova = window.prompt("Nova data de reinício (AAAA-MM-DD)", hoje);
    if (!nova || !/^\d{4}-\d{2}-\d{2}$/.test(nova)) {
      toast.error("Informe a nova data de reinício para retomar o job");
      return;
    }
    // RF-65 — o servidor registra a data e desloca a OS na mesma operação.
    retomar.mutate(
      { cardId: card.id, dataReinicio: nova },
      {
        onSuccess: ({ deslocamento }) =>
          toast.success("Job retomado", {
            description: deslocamento.slots
              ? `${deslocamento.slots} slot(s) reprogramados em ${deslocamento.deltaDias > 0 ? "+" : ""}${deslocamento.deltaDias} dias`
              : "Nenhum slot alocado para reprogramar.",
          }),
        onError: (e) => toast.error(mensagem(e)),
      },
    );
  };

  const previstoHoras = (card.escopo?.horasConteudo ?? 0) + (card.escopo?.horasDesign ?? 0);

  return (
    <div
      ref={setNodeRef}
      data-os={card.os}
      className="group rounded-lg border border-border bg-secondary/40 p-3 transition hover:border-primary/40 hover:bg-secondary"
      style={{
        borderLeftColor: card.coordenador?.cor ?? "#94A3B8",
        borderLeftWidth: 3,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: arrastando ? 0.4 : 1,
        touchAction: "none",
      }}
    >
      {/*
        A alça de arraste é só a área de conteúdo, e vai sem os `attributes` do
        dnd-kit: eles marcam o nó com `role="button"` e `aria-disabled`, o que
        anuncia o card inteiro como desabilitado para quem não pode arrastar —
        inclusive o link que leva ao card. O arraste aqui é por ponteiro.
      */}
      <div {...listeners} style={{ cursor: travado ? "default" : "grab" }}>
        <Link to="/card/$id" params={{ id: card.id }} className="block">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span className="truncate">{card.os}</span>
            <span className="text-muted-foreground/60">·</span>
            {card.frente}
          </div>
          <div className="mt-1 text-xs font-semibold text-foreground">{card.cliente.nome}</div>
          <div className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">{card.nome}</div>

          <div className="mt-2.5 flex flex-wrap gap-1">
            <Etiqueta>{card.tipoProjeto}</Etiqueta>
            {!!card.escopo?.ivs && <Etiqueta destaque>{card.escopo.ivs} IV</Etiqueta>}
            {!!card.escopo?.adaptacoesIV && (
              <Etiqueta destaque>{card.escopo.adaptacoesIV} Adapt. IV</Etiqueta>
            )}
            {card.idioma !== "pt-BR" && (
              <Etiqueta>
                <Languages className="mr-1 h-2.5 w-2.5" />
                {card.idioma}
              </Etiqueta>
            )}
          </div>

          {/* RF-13 — o card fica na coluna mais avançada e sinaliza a paralela. */}
          {card.fasesParalelas.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <GitBranch className="h-3 w-3 shrink-0 text-health-yellow" />
              {card.fasesParalelas.map((fase) => (
                <span
                  key={fase}
                  title="Fase em andamento em paralelo à coluna atual"
                  className="rounded-md bg-health-yellow/15 px-1.5 py-0.5 text-[10px] font-medium text-health-yellow"
                >
                  {ROTULO_ETAPA[fase] ?? fase} em paralelo
                </span>
              ))}
            </div>
          )}

          {/* Previsto × realizado — só o confirmado conta, sem conversão (RF-66). */}
          <div className="mt-2.5 space-y-1.5">
            <LinhaProgresso
              rotulo="Slides"
              feito={card.realizado.slides}
              previsto={card.escopo?.slides ?? 0}
              sufixo=""
            />
            <LinhaProgresso
              rotulo="Horas"
              feito={card.realizado.horas}
              previsto={previstoHoras}
              sufixo="h"
            />
          </div>

          <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Film className="h-3 w-3" /> {card.escopo?.slides ?? 0} slides
            </span>
            {card.prazos?.vFinal && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(`${card.prazos.vFinal}T00:00:00Z`).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "short",
                  timeZone: "UTC",
                })}
              </span>
            )}
          </div>

          <div className="mt-2.5 flex items-center justify-between border-t border-border/60 pt-2.5">
            <div className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: card.coordenador?.cor ?? "#94A3B8" }}
              />
              <span className="text-[10px] text-muted-foreground">
                {card.coordenador?.nome.split(" ")[0] ?? "sem coordenador"}
              </span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Users className="h-3 w-3" />
              <div className="flex -space-x-1.5">
                {card.equipe.slice(0, 3).map((m) => (
                  <div
                    key={`${m.papel}-${m.profissional.id}`}
                    title={`${m.papel}: ${m.profissional.nome}`}
                    className="grid h-5 w-5 place-items-center rounded-full border border-card bg-secondary text-[9px] font-semibold text-foreground"
                  >
                    {m.profissional.inicial}
                  </div>
                ))}
                {card.equipe.length === 0 && (
                  <span className="text-muted-foreground">sem equipe</span>
                )}
              </div>
            </div>
          </div>
        </Link>
      </div>

      {podeMover && (
        <div className="mt-2 flex justify-end border-t border-border/60 pt-2">
          {card.pausado ? (
            <button
              onClick={aoRetomar}
              disabled={retomar.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-[10px] font-medium text-primary hover:bg-primary/20 disabled:opacity-50"
            >
              <Play className="h-3 w-3" /> Retomar
            </button>
          ) : (
            <button
              onClick={() =>
                pausar.mutate(card.id, {
                  onSuccess: () =>
                    toast.warning("Job pausado · sai do fluxo ativo do Flow", {
                      description: card.os,
                    }),
                  onError: (e) => toast.error(mensagem(e)),
                })
              }
              disabled={pausar.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-medium text-muted-foreground hover:bg-secondary disabled:opacity-50"
            >
              <Pause className="h-3 w-3" /> Pausar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LinhaProgresso({
  rotulo,
  feito,
  previsto,
  sufixo,
}: {
  rotulo: string;
  feito: number;
  previsto: number;
  sufixo: string;
}) {
  const pct = previsto > 0 ? Math.min(100, Math.round((feito / previsto) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{rotulo}</span>
        <span className="tabular-nums text-foreground">
          {feito}
          {sufixo}/{previsto}
          {sufixo}
        </span>
      </div>
      <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Etiqueta({
  children,
  destaque = false,
}: {
  children: React.ReactNode;
  destaque?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium",
        destaque ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}
